/* ============================================================================
   ÉTAPE PRODUIT — el estado « ya está hecho »
   ----------------------------------------------------------------------------
   CREATE → MADE ya retiraba las herramientas. Pero retirar herramientas no es
   un estado: era el editor sin controles, y el cliente no tenía por qué
   entender que su producto estaba terminado ni qué podía hacer a continuación.
   Se salía con Escape, que es conocimiento previo, no interfaz.

   Esto convierte ese momento en un SITIO:

     · el producto manda y respira un poco más
     · dos decisiones explícitas — modificar o llevárselo
     · inspección de verdad: las caras que existan, sin tocar el diseño
     · una firma discreta, porque lo ha hecho quien lo mira

   Inspeccionar NO es editar. Cambiar de cara aquí mira la otra parte de la
   prenda; no selecciona, no mueve y no abre un panel. El DesignSession no se
   entera.

   Módulo aparte: studio.js ya pasa de 3 000 líneas, y esto tiene que poder
   viajar entero cuando el Studio se monte dentro del storefront.
   ========================================================================== */

const ETAPE_PRODUIT = (() => {
'use strict';

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

let _api = null;      // lo que el Studio nos deja usar
let _caja = null;

/* ------------------------------------------------------------ montaje --- */

function construir() {
  if (_caja) return _caja;

  const caja = document.createElement('section');
  caja.className = 'etape';
  caja.setAttribute('aria-label', 'Votre produit');

  caja.innerHTML = `
    <p class="etape__signature" data-etape-signature>Créé par vous</p>
    <div class="etape__vues" data-etape-vues role="group" aria-label="Voir les faces"></div>
    <div class="etape__actions">
      <button type="button" class="btn etape__modifier" data-etape-modifier>Modifier</button>
      <button type="button" class="btn btn--or etape__panier" data-etape-panier>Ajouter au panier</button>
      <button type="button" class="btn etape__variante" data-etape-variante
              title="Repartir de cette création sans la perdre">Créer une variante</button>
      <button type="button" class="btn partager" data-etape-partager
              aria-expanded="false">Partager</button>
    </div>`;

  document.body.append(caja);
  _caja = caja;

  /* Compartir una creación es el final natural del bucle: acabas de hacer
     algo tuyo y lo primero que quieres es enseñarlo. La píldora se transforma
     en su lugar; los destinos son sólo los que existen de verdad — el menú
     nativo del teléfono y copiar el enlace—. Ver `partager.js`. */
  if (typeof PARTAGER !== 'undefined') {
    PARTAGER.montar(caja.querySelector('[data-etape-partager]'), {
      title: 'Ma création EDISAMP',
      text: 'Je viens de créer cette pièce sur EDISAMP.',
    });
  }

  caja.querySelector('[data-etape-modifier]').addEventListener('click', () => {
    if (_api && _api.modifier) _api.modifier();
  });
  caja.querySelector('[data-etape-panier]').addEventListener('click', () => {
    if (_api && _api.ajouterAuPanier) _api.ajouterAuPanier();
  });
  caja.querySelector('[data-etape-variante]').addEventListener('click', () => {
    if (_api && _api.creerVariante) _api.creerVariante();
  });

  return caja;
}

/* ------------------------------------------------------- inspección ----- */

/**
 * Las caras que se pueden mirar. Sólo las que EXISTEN: un producto de una
 * sola vista no enseña un selector con un botón, y una taza no enseña
 * devant/dos — su inspección es girarla, y ya la tiene.
 */
function pintarVues(vues, vueActiva, conDiseno) {
  const caja = $('[data-etape-vues]', _caja);
  if (!caja) return;

  if (!vues || vues.length < 2) { caja.hidden = true; caja.replaceChildren(); return; }
  caja.hidden = false;

  caja.replaceChildren(...vues.map((v) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'etape__vue';
    b.dataset.vue = v.id;
    b.setAttribute('aria-pressed', String(v.id === vueActiva));
    b.textContent = v.nom || v.id;
    /* El punto sale de contar los elementos de esa cara en la sesión. No es
       decoración: dice dónde hay diseño y dónde no. */
    if (conDiseno && conDiseno(v.id)) {
      const p = document.createElement('span');
      p.className = 'etape__point';
      p.setAttribute('aria-hidden', 'true');
      b.append(p);
      b.setAttribute('aria-description', 'Cette face contient un design');
    }
    b.addEventListener('click', () => {
      if (_api && _api.inspecterVue) _api.inspecterVue(v.id);
    });
    return b;
  }));
}

/* ---------------------------------------------------------- entrar/salir - */

function entrer(datos = {}) {
  construir();
  document.body.dataset.etape = 'produit';
  pintarVues(datos.vues, datos.vueActive, datos.conDiseno);

  const variante = $('[data-etape-variante]', _caja);
  if (variante) variante.hidden = !(_api && _api.creerVariante);

  // El foco entra en la decisión principal: quien navega con teclado no tiene
  // que buscarla, y quien no, no nota nada.
  const primero = $('[data-etape-panier]', _caja);
  if (primero) setTimeout(() => { try { primero.focus({ preventScroll: true }); } catch (e) {} }, 60);
}

function sortir() {
  delete document.body.dataset.etape;
}

const dansEtape = () => document.body.dataset.etape === 'produit';

/** Repinta el selector de caras sin salir del estado. */
function majVues(datos = {}) {
  if (!_caja || !dansEtape()) return;
  pintarVues(datos.vues, datos.vueActive, datos.conDiseno);
}

/**
 * Una nota momentánea en el sitio de la firma.
 *
 * Crear una variante tiene que decirse: si no, el cliente sigue diseñando sin
 * saber que ahora está en otra creación. Y se dice DONDE ya estaba mirando,
 * no en un aviso que se cruza por delante del producto.
 */
let _reloj = null;
function noter(texto, ms = 3200) {
  const p = _caja && $('[data-etape-signature]', _caja);
  if (!p) return;
  const antes = p.dataset.base || p.textContent;
  p.dataset.base = antes;
  p.textContent = texto;
  p.dataset.note = 'true';
  clearTimeout(_reloj);
  _reloj = setTimeout(() => {
    p.textContent = p.dataset.base || antes;
    delete p.dataset.note;
  }, ms);
}

function monter(api) { _api = api; construir(); return { entrer, sortir, majVues, dansEtape, noter }; }

return { monter, entrer, sortir, majVues, dansEtape, noter };
})();

if (typeof window !== 'undefined') window.ETAPE_PRODUIT = ETAPE_PRODUIT;
